/**
 * useBrowserNotifications — Phase 2.
 *
 * Composes browser-permission state + server-side preferences
 * (`users.browserNotificationsEnabled` + `users.notificationPromptDismissedAt`)
 * + tab-focus state into a single `notify(args)` gate that fires
 * `new Notification(...)` only when ALL conditions are met:
 *
 *   - `Notification.permission === 'granted'`
 *   - `enabled === true` (server-side toggle)
 *   - tab is NOT focused (so the user actually gets cued; in-focus
 *     messages already render in the existing chat UI)
 *
 * `requestPermission()` invokes the browser prompt and, on `'granted'`,
 * flips `enabled` to true server-side as a convenience so the user
 * doesn't have to toggle twice. `dismissPrompt()` stamps a timestamp on
 * the server so the opt-in banner doesn't re-appear in a fresh tab.
 *
 * Server-side state comes from `useMe()`; mutations route through
 * `useUpdateMyProfile()` which already invalidates the `me` cache on
 * success.
 *
 * SSR / very-old-browser safety: `typeof Notification === 'undefined'`
 * short-circuits to `permission: 'denied'` and a no-op `notify`.
 *
 * Web Push layer (ADR-034): on top of the foreground `new Notification(...)`
 * path above, this hook also drives Web Push subscription lifecycle off the
 * SAME single `browserNotificationsEnabled` toggle — there is intentionally
 * NO parallel push-specific toggle. On permission grant it registers the
 * service worker + subscribes the push manager + POSTs the subscription; on
 * toggle-off it unsubscribes + DELETEs. A mount-time reconcile re-subscribes
 * when permission is granted + the toggle is on but the browser dropped the
 * subscription (covers the SW `pushsubscriptionchange` drift, which
 * deliberately does not re-POST from the SW — see `public/sw.js`). Push
 * covers the away / tab-closed / machine-asleep case; the foreground
 * Notification path covers tab-open-but-unfocused. All push work is
 * capability-guarded + best-effort: a push failure never breaks the
 * foreground path or the toggle.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useMe } from '@/features/me/use-me'
import { useUpdateMyProfile } from '@/features/tenant-settings/use-my-profile'

import {
  hasActivePushSubscription,
  subscribeToPush,
  supportsPush,
  unsubscribeFromPush,
} from './push-subscription'

const supportsNotifications = (): boolean =>
  typeof window !== 'undefined' && typeof window.Notification !== 'undefined'

const readPermission = (): NotificationPermission => {
  if (!supportsNotifications()) return 'denied'
  return window.Notification.permission
}

const readTabFocus = (): boolean => {
  if (typeof document === 'undefined') return false
  return document.hasFocus()
}

export interface NotifyArgs {
  title: string
  body: string
  icon?: string
  tag: string
  onClick?: () => void
}

export interface UseBrowserNotificationsReturn {
  permission: NotificationPermission
  enabled: boolean
  promptDismissedAt: string | null
  isTabFocused: boolean
  requestPermission: () => Promise<NotificationPermission>
  setEnabled: (next: boolean) => Promise<void>
  dismissPrompt: () => Promise<void>
  notify: (args: NotifyArgs) => void
}

export function useBrowserNotifications(): UseBrowserNotificationsReturn {
  const me = useMe()
  const update = useUpdateMyProfile()

  const enabled = Boolean(me.data?.user.browserNotificationsEnabled)
  const promptDismissedAt = me.data?.user.notificationPromptDismissedAt ?? null

  const [permission, setPermission] = useState<NotificationPermission>(readPermission)
  const [isTabFocused, setIsTabFocused] = useState<boolean>(readTabFocus)

  // Subscribe to permission changes via Permissions API where available.
  // Chrome + Firefox surface the `notifications` permission through
  // navigator.permissions; Safari does not, so we additionally re-read
  // Notification.permission on every focus event as a fallback.
  useEffect(() => {
    if (!supportsNotifications()) return
    let cancelled = false
    let status: PermissionStatus | null = null

    const onChange = () => {
      if (cancelled) return
      setPermission(readPermission())
    }

    const subscribe = async () => {
      try {
        const perms = navigator.permissions
        if (!perms || typeof perms.query !== 'function') return
        status = await perms.query({ name: 'notifications' as PermissionName })
        if (cancelled) return
        status.addEventListener('change', onChange)
      } catch {
        // Some browsers throw on unknown permission names — fine, we
        // fall back to the focus-event polling below.
      }
    }
    void subscribe()

    return () => {
      cancelled = true
      if (status) status.removeEventListener('change', onChange)
    }
  }, [])

  // Tab-focus tracking — `document.hasFocus()` is the source of truth,
  // re-read on focus / blur / visibilitychange. The `notify` gate uses
  // a ref to read the latest value so a delayed (queued) frame still
  // sees fresh state.
  const isTabFocusedRef = useRef(isTabFocused)
  useEffect(() => {
    isTabFocusedRef.current = isTabFocused
  }, [isTabFocused])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => {
      setIsTabFocused(readTabFocus())
      // Re-read permission on focus as a Safari-fallback for the
      // Permissions API gap above.
      setPermission(readPermission())
    }
    window.addEventListener('focus', sync)
    window.addEventListener('blur', sync)
    document.addEventListener('visibilitychange', sync)
    return () => {
      window.removeEventListener('focus', sync)
      window.removeEventListener('blur', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  const setEnabled = useCallback(
    async (next: boolean) => {
      await update.mutateAsync({ browserNotificationsEnabled: next })
      // Web Push lifecycle rides the same toggle. Best-effort + capability
      // guarded: a failure here (BE route not deployed yet, permission
      // revoked, old browser) never blocks the server-side toggle flip.
      if (!supportsPush()) return
      try {
        if (next) {
          // Only subscribe if the browser permission is actually granted —
          // turning the toggle on from Settings with permission `default`
          // goes through `requestPermission` instead; `denied` can't
          // subscribe at all.
          if (readPermission() === 'granted') await subscribeToPush()
        } else {
          await unsubscribeFromPush()
        }
      } catch {
        // Non-fatal: the server toggle is the source of truth. A failed
        // subscribe self-heals on the next mount-time reconcile; a failed
        // unsubscribe leaves a stale endpoint the BE prunes on push 404/410.
      }
    },
    [update],
  )

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!supportsNotifications()) return 'denied'
    const result = await window.Notification.requestPermission()
    setPermission(result)
    if (result === 'granted') {
      try {
        // Flip the server toggle first (the convenience that spares a second
        // click), then subscribe to Web Push. `setEnabled(true)` itself
        // subscribes when permission is already granted — but at this exact
        // moment `readPermission()` may still read the pre-grant value in
        // some browsers, so we subscribe explicitly here to be certain.
        await update.mutateAsync({ browserNotificationsEnabled: true })
        if (supportsPush()) {
          try {
            await subscribeToPush()
          } catch {
            // Push subscribe is non-fatal — foreground notifications still
            // work, and the mount reconcile retries the subscription.
          }
        }
      } catch {
        // Server failure is non-fatal here — the local permission grant
        // stands; the user can flip the toggle from Settings later.
      }
    }
    return result
  }, [update])

  const dismissPrompt = useCallback(async () => {
    await update.mutateAsync({ notificationPromptDismissedAt: new Date().toISOString() })
  }, [update])

  // Mount-time push reconcile (ADR-034). When the user has opted in
  // (`enabled`) AND the browser permission is granted but there's no active
  // push subscription, re-subscribe. This covers:
  //   - `pushsubscriptionchange` drift: the SW re-subscribes the browser but
  //     deliberately doesn't re-POST to the server (no auth from the SW), so
  //     the server may be missing the new endpoint — re-POST it here.
  //   - a user who opted into foreground notifications on an older build
  //     (pre-push) and is now visiting with push support: upgrade them to
  //     push without a second prompt.
  //   - a browser that dropped the subscription entirely.
  // Guarded so it only fires once the relevant state is settled; best-effort.
  const reconciledRef = useRef(false)
  useEffect(() => {
    if (!supportsPush()) return
    if (!enabled) {
      // Allow a fresh reconcile if the user re-enables later in this session.
      reconciledRef.current = false
      return
    }
    if (permission !== 'granted') return
    if (reconciledRef.current) return
    reconciledRef.current = true

    let cancelled = false
    void (async () => {
      try {
        const active = await hasActivePushSubscription()
        if (cancelled || active) return
        await subscribeToPush()
      } catch {
        // Non-fatal; foreground notifications still work. Let a future
        // mount retry by clearing the guard.
        reconciledRef.current = false
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, permission])

  const notify = useCallback(
    (args: NotifyArgs) => {
      if (!supportsNotifications()) return
      if (permission !== 'granted') return
      if (!enabled) return
      if (isTabFocusedRef.current) return

      const notification = new window.Notification(args.title, {
        body: args.body,
        icon: args.icon,
        tag: args.tag,
      })

      if (args.onClick) {
        notification.onclick = () => {
          if (typeof window !== 'undefined') window.focus()
          args.onClick?.()
          notification.close()
        }
      }
    },
    [permission, enabled],
  )

  return {
    permission,
    enabled,
    promptDismissedAt,
    isTabFocused,
    requestPermission,
    setEnabled,
    dismissPrompt,
    notify,
  }
}
